import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@akuna/ui";
import { AlertCircle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function ResetPassword() {
    const { t } = useTranslation("auth");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState("");
    const [isCheckingSession, setIsCheckingSession] = useState(true);
    const { updatePassword, session, loading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // Wait for auth to finish loading before checking session
        if (!loading) {
            setIsCheckingSession(false);

            // Only redirect if no session and no hash in URL (not coming from email link)
            const hasHash = window.location.hash.includes('access_token') || window.location.hash.includes('recovery');

            if (!session && !hasHash) {
                navigate("/forgot-password", { replace: true });
            }
        }
    }, [session, loading, navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError("");

        // Validate passwords match
        if (password !== confirmPassword) {
            setError(t("errors.passwordMismatch"));
            return;
        }

        // Validate password length
        if (password.length < 8) {
            setError(t("errors.passwordMinLength"));
            return;
        }

        setIsLoading(true);

        const { error } = await updatePassword(password);

        if (!error) {
            setIsSuccess(true);
            // Redirect to login after 3 seconds
            setTimeout(() => {
                navigate("/login");
            }, 3000);
        } else {
            setError(error);
        }

        setIsLoading(false);
    };

    if (isCheckingSession || loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
                <div className="w-full max-w-md text-center">
                    <p className="text-muted-foreground">{t("common:loading")}</p>
                </div>
            </div>
        );
    }

    if (isSuccess) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
                <div className="w-full max-w-md">
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-block mb-6">
                            <h1 className="text-3xl font-bold text-primary">Akuna</h1>
                        </Link>
                        <h2 className="text-2xl font-bold text-foreground mb-2">{t("resetPasswordSuccessTitle")}</h2>
                        <p className="text-muted-foreground">
                            {t("resetPasswordSuccessSubtitle")}
                        </p>
                    </div>

                    <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
                        <div className="text-center space-y-4">
                            <div className="flex justify-center">
                                <div className="rounded-full bg-green-100 dark:bg-green-900/20 p-3">
                                    <CheckCircle2 className="h-12 w-12 text-green-600 dark:text-green-400" />
                                </div>
                            </div>

                            <p className="text-sm text-muted-foreground">
                                {t("resetPasswordRedirecting")}
                            </p>

                            <Button
                                className="w-full"
                                asChild
                            >
                                <Link to="/login">{t("goToLoginNow")}</Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block mb-6">
                        <h1 className="text-3xl font-bold text-primary">Akuna</h1>
                    </Link>
                    <h2 className="text-2xl font-bold text-foreground mb-2">{t("resetPasswordTitle")}</h2>
                    <p className="text-muted-foreground">
                        {t("resetPasswordSubtitle")}
                    </p>
                </div>

                <div className="bg-card rounded-lg shadow-lg p-8 border border-border">
                    {error && (
                        <Alert variant="danger" className="mb-4">
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>{error}</AlertDescription>
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="password">{t("newPassword")}</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder={t("newPasswordPlaceholder")}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    disabled={isLoading}
                                    className="pr-12"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none"
                                    aria-label={showPassword ? t("hidePassword") : t("showPassword")}
                                    disabled={isLoading}
                                >
                                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirmPassword">{t("confirmNewPassword")}</Label>
                            <div className="relative">
                                <Input
                                    id="confirmPassword"
                                    type={showConfirmPassword ? "text" : "password"}
                                    placeholder={t("confirmPasswordPlaceholder")}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={isLoading}
                                    className="pr-12"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                                    className="absolute inset-y-0 right-3 flex items-center text-muted-foreground hover:text-foreground focus:outline-none"
                                    aria-label={showConfirmPassword ? t("hidePassword") : t("showPassword")}
                                    disabled={isLoading}
                                >
                                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <Button type="submit" className="w-full" disabled={isLoading}>
                            {isLoading ? t("updatingPassword") : t("updatePassword")}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
